import moment from 'moment';
import TimesheetWeek from '../../api/models/timesheetWeek';
// import { maxAllowedSingleEntryMinutes } from '../../common/constants';
import TimesheetWeekEntry from '../../api/models/TimesheetWeekEntry';
import DayEntry from './types/dayEntry';
import EntryState from './types/entryState';
import TimesheetTaskEntries from './types/timesheetTaskEntries';


export const getOtherMinutesForDay = (
  districtId: number,
  timesheetWeek: TimesheetWeek,
  exclusionTasks: TimesheetTaskEntries[],
  entryIndex: number,
  entryDateOf: moment.Moment,
): number => {
  let totalMinutes = 0;

  if (!timesheetWeek?.items) return 0;

  timesheetWeek.items
    .filter(item => item.districtId === districtId)
    .forEach(item => {
      item.tasks.forEach(task => {
        const entry = task.entries.filter(taskentry =>
          moment(taskentry.dateOfEntry).isSame(entryDateOf, 'day')
        )[0]
        if (!entry?.dateOfEntry) return;

        const entryDate = moment(entry.dateOfEntry).format("YYYY-MM-DD");

        const excluded = exclusionTasks.some(exTask => {
          const exEntry = exTask.entries[entryIndex];
          const exEntryDate = moment(exEntry.dateOfEntry).format("YYYY-MM-DD");
          if (entryDate !== exEntryDate) return true;

          if (entry.billToId === 1 && exEntry?.billToId === 1) return true;

          return (
            exEntry &&
            ((exEntry?.entryId === entry?.entryId || exEntry?.timesheetEntryId === entry?.entryId) ||
              (exTask.billToId === entry?.billToId && exTask?.taskTypeId === task?.taskTypeId)) &&
            exTask?.schoolId === item?.schoolId);
        });

        if (!excluded) {
          totalMinutes += entry.minutes ?? 0;
        }
      });
    });

  return totalMinutes;
};

export const validateOnTaskTypeChange = (
  timesheetTasks: TimesheetTaskEntries[],
  entryState: EntryState,
): string | null => {
  if (timesheetTasks.length === 0) return '';

  let dailyAllowance = entryState.dailyAllowance ?? 0;
  let unlimitedOverageAllowed = entryState.unlimitedOverageAllowed ?? false;
  let overageAllowance = entryState.dailyOverageAllowance ?? 0;

  const dayWiseTotal: number[] = [0, 0, 0, 0, 0];

  for (let task of timesheetTasks) {
    for (let i = 0; i < 5; i++) {
      const minutes = task?.entries[i]?.minutes ?? 0;

      // if (minutes > maxAllowedSingleEntryMinutes) {
      //   entryState.hasValidationErrors = true;
      //   return `Minutes entered are greater than permitted for a single entry.\nPlease enter a number lower than ${maxAllowedSingleEntryMinutes + 1}.`;
      // }

      dayWiseTotal[i] += minutes;
    }
  }

  if (!unlimitedOverageAllowed) {

    for (let i = 0; i < dayWiseTotal.length; i++) {
      const dayTotal = dayWiseTotal[i];
      const entryDateOf = timesheetTasks![0]?.entries[i].dateOfEntry;
      const otherMinutes = getOtherMinutesForDay(entryState?.districtId!, entryState?.selectedTimesheet!, timesheetTasks, i, entryDateOf);
      const totalWithOther = dayTotal + otherMinutes;

      if (totalWithOther > dailyAllowance + overageAllowance) {
        entryState.hasValidationErrors = true;
        return `Daily time allowance${overageAllowance > 0 ? ' plus overage' : ''} exceeded.\nAdditional time entry is not allowed.`;
      }
    }
  }

  entryState.hasValidationErrors = false;
  return null;
};


export const getRequestedMinutesForDay = (
  timesheetTasks: TimesheetTaskEntries[],
  dayEntry: DayEntry | TimesheetWeekEntry,
  entryindex: number,
): { sum: number, listOfRequestedEntries: number[] } => {
  if (!dayEntry) return { sum: 0, listOfRequestedEntries: [] };

  let dayEntryMinutesSum = 0;
  let minutesArray: number[] = [];

  timesheetTasks.forEach((entry) => {
    const minutes = entry?.entries?.[entryindex]?.minutes ?? 0;
    minutesArray.push(minutes);
    dayEntryMinutesSum += minutes;
  });

  return { sum: dayEntryMinutesSum, listOfRequestedEntries: minutesArray };
};

export const validateEntryMinutesForDay = (
  entryState: EntryState,
  entries: DayEntry[] | TimesheetWeekEntry[],
  requestedMinutes: number,
): string | null => {
  // Guard clause to short-circuit heavier calculations if input minutes exceed max permitted
  // if (
  //   !entryState.unlimitedOverageAllowed &&
  //   requestedMinutes > maxAllowedSingleEntryMinutes
  // ) {
  //   entryState.hasValidationErrors = true;
  //   return `Minutes entered are greater than permitted for a single entry.\nPlease enter a number lower than ${maxAllowedSingleEntryMinutes + 1}.`;
  // }

  for (let entryIndex = 0; entryIndex < entries.length; entryIndex++) {
    const entry = entries[entryIndex];
    const entryDateOf = entryState?.timesheetEntryTasks![0]?.entries[entryIndex].dateOfEntry;

    let totalRequestedMinutes = getRequestedMinutesForDay(
      entryState?.timesheetEntryTasks!,
      entry,
      entryIndex
    );

    // const hasEntryExceedingLimit = totalRequestedMinutes?.listOfRequestedEntries?.some(
    //   (minutes) => minutes > maxAllowedSingleEntryMinutes
    // );

    // if (hasEntryExceedingLimit) {
    //   entryState.hasValidationErrors = true;
    //   return `Minutes entered are greater than permitted for a single entry.\nPlease enter a number lower than ${maxAllowedSingleEntryMinutes + 1}.`;
    // }
    let totalRequestedDailyMinutes = totalRequestedMinutes?.sum ?? 0; // Initialize to user input (for when no timesheet minutes exist yet)
    let dailyAllowance = entryState.dailyAllowance ?? 0;
    let dailyOverageAllowance = entryState.dailyOverageAllowance ?? 0;
    let unlimitedOverageAllowed = entryState.unlimitedOverageAllowed ?? false;

    // If minutes already have been entered on timesheet
    if (entryState.districtId && entryState.selectedTimesheet) {
      // Calculate existing minutes by summing all entry minutes for that day
      let totalOtherDailyMinutes = getOtherMinutesForDay(
        entryState.districtId,
        entryState.selectedTimesheet,
        entryState?.timesheetEntryTasks!,
        entryIndex,
        entryDateOf,
      );


      // Add day's existing minutes to current user input minutes; subtract previous input minutes
      totalRequestedDailyMinutes = totalOtherDailyMinutes + totalRequestedDailyMinutes;


    }

    // Total requested minutes are over the district's daily allowance along with overage
    if (totalRequestedDailyMinutes > (dailyAllowance + dailyOverageAllowance)) {
      if (!unlimitedOverageAllowed) {
        entryState.hasValidationErrors = true;
        return `Daily time allowance${dailyOverageAllowance > 0 ? ' plus overage' : ''} exceeded.\nAdditional time entry is not allowed.`;
      }
    }
  };

  entryState.hasValidationErrors = false;
  return null;
};
